import { Box } from '@/components/box/box';
import { EnterAnimation } from '@/components/enter-animation/enter-animation';
import { GridContainer } from '@/components/grid-container/grid-container';
import { Layout } from '@/components/layout/layout';
import { StaggeredText } from '@/components/staggered-text/staggered-text';
import { Text } from '@/components/text/text';
import { WorkSection } from '@/components/work-section/work-section';
import { slugify } from '@/utils/slugify';
import type {
  GetStaticPaths,
  GetStaticProps,
  InferGetStaticPropsType,
} from 'next';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { urlForImage } from '@/lib/sanity/sanity';
import { CaseTransitionIn } from '@/components/case-transitions/case-transitions';
import { useCaseTransitionStore } from '@/hooks/use-case-transition-store';
import { Badge, getBadge } from '@/lib/sanity/queries/get-badge';
import { getData } from '@/lib/sanity/get-data';
import { groq } from 'next-sanity';
import { z } from 'zod';
import { MetadataSchema } from '@/lib/schemas/metadata';
import { WorkSectionSchema } from '@/lib/schemas/work-section';
import { WorkNavigation } from '@/components/work-navigation/work-navigation';

function WorkOverview({
  work,
  badge,
}: InferGetStaticPropsType<typeof getStaticProps>) {
  const enterDelay = 0.4;

  /**
   * This sets the active Category based on the slug read from useRouter()
   * If it can't match the slug to sanity, it defaults to the first item in the list
   * In the future if we redirect before getting a render, we can remove some of these protections.
   * Frankly, I quite like defensive programming anyway
   */
  const router = useRouter();
  const [isAnimated, setIsAnimated] = useState(false);

  const { query } = router;
  const { category: categorySlug } = query;
  const { sections, title, metadata } = work;

  const { isCaseTransitionReady, resetCaseTransitionProps } =
    useCaseTransitionStore();

  useEffect(() => {
    resetCaseTransitionProps();
  }, [resetCaseTransitionProps]);

  // This code is used to hide certain parts of the template if you're on a drilldown page
  // Use case: Send a link with certain projects to clients, limiting their views
  const isDrillDownPage =
    Array.isArray(categorySlug) && categorySlug.length > 1;

  const activeSection =
    categorySlug && categorySlug[0]
      ? work.sections.find((item) => {
          return slugify(item.category.name) === categorySlug[0];
        }) || work.sections[0]
      : work.sections[0];

  /**
   * By requesting the image through the next.js image proxy, we save on traffic to the Sanity CDN
   */
  const ogImage = metadata?.openGraphImage
    ? urlForImage(metadata.openGraphImage).width(1200).quality(75).url()
    : 'https://cleverfranke.com/api/og?title=Work';

  return (
    <>
      <Layout
        title={metadata?.title || 'Work'}
        ogImage={ogImage}
        description={metadata?.description || undefined}
        badgeData={badge}
      >
        <Box css={{ padding: '$12 0' }}>
          <GridContainer>
            <Text
              as="h1"
              size={{
                '@initial': 8,
                '@bp1': 11,
                '@bp2': 12,
                '@bp4': 13,
                '@bp7': 15,
              }}
              css={{
                mb: '$10',
                gridColumn: 'main',
                '@bp4': {
                  gridColumn: '7 / span 10',
                },
                '@bp5': {
                  gridColumn: '6 / span 11',
                },
                '@bp8': {
                  gridColumn: '6 / span 8',
                  mb: '$11',
                },
              }}
            >
              <StaggeredText
                text={title}
                startColor="$foreground"
                enterDelayInSeconds={enterDelay}
              />
            </Text>
          </GridContainer>

          {!isDrillDownPage && (
            <GridContainer>
              <Box
                css={{
                  gridColumn: 'main',
                  '@bp4': {
                    gridColumn: '7 / span 10',
                  },
                  '@bp5': {
                    gridColumn: '6 / span 11',
                  },
                }}
              >
                <EnterAnimation enterDelayInSeconds={1.25}>
                  <WorkNavigation
                    selectedSection={activeSection}
                    sections={sections}
                  />
                </EnterAnimation>
              </Box>
            </GridContainer>
          )}

          <EnterAnimation
            enterDelayInSeconds={1.25}
            onAnimationComplete={(value) => {
              setIsAnimated(value);
            }}
          >
            <WorkSection
              activeSubsectionTitle={
                isDrillDownPage && categorySlug ? categorySlug[1] : undefined
              }
              key={activeSection._key}
              section={activeSection}
              canAnimate={isAnimated}
            />
          </EnterAnimation>
        </Box>
      </Layout>

      {isCaseTransitionReady() && <CaseTransitionIn />}
    </>
  );
}

const WorkCategoryNamesSchema = z.array(
  z.object({
    name: z.string(),
  })
);

export const getStaticPaths: GetStaticPaths = async () => {
  const workCategoryNames = await getData(
    groq`
    *[_type == "workCategory"] {
      name,
    }
    `,
    WorkCategoryNamesSchema,
    false
  );

  const paths = workCategoryNames.map((category) => {
    return {
      params: {
        category: [slugify(category.name)],
      },
    };
  });

  return {
    paths,
    fallback: 'blocking',
  };
};

const WorkOverviewSchema = z.object({
  title: z.optional(z.string()),
  metadata: z.optional(MetadataSchema),
  sections: z.array(WorkSectionSchema),
});

const getWorkOverview = (preview: boolean) => {
  return getData(
    groq`
    *[_type == "work"][0] {
      title,
      metadata {
        title, description, openGraphImage
      },
      sections[] {
        _key,
        title,
        category -> {
          _id,
          name
        },
        subsections[] {
          _key,
          title,
          hasLimit, limitNumber,

          subcategory -> {
            name,
            description,
          },
          projects[target -> isActive != false] {
            _key,
            display,
            images[] {
              asset -> {
                _id,
                metadata {
                  palette
                }
              }
            },
            target -> {
              _id,
              title, subtitle, slug,
              cover {
                asset -> {
                  _id,
                  metadata {
                    dimensions, palette
                  }
                }
              },
              workOverviewImage {
                asset -> {
                  _id,
                  metadata {
                    dimensions, palette
                  }
                }
              }  
            }
          }
        }
      }
    }
  `,
    WorkOverviewSchema,
    preview
  );
};

export const getStaticProps: GetStaticProps<{
  work: z.infer<typeof WorkOverviewSchema>;
  badge: Badge;
}> = async (context) => {
  const { preview = false } = context;

  const work = await getWorkOverview(preview);
  const badge = await getBadge(preview);

  return {
    props: { work, badge },
    //           s * min
    revalidate: 60 * 30,
  };
};

export default WorkOverview;
